/**
 * Shared utilities for tracking which discount modals have been shown.
 * Used by both AppliedOffers and RestrictedDiscountApplied components to prevent
 * showing the same modal twice in a session.
 *
 * POS-13717: Consolidated storage key ensures both bundle/mix-and-match offers
 * and regular sale discounts share the same "shown" state.
 */

const DISCOUNT_MODAL_SHOWN_KEY = 'dutchie_discount_modal_shown';

/**
 * Get set of discount IDs that have already shown their modal
 * @returns {Set<string>} Set of discount IDs that have been shown
 */
export const getShownDiscountIds = () => {
  try {
    const stored = sessionStorage.getItem(DISCOUNT_MODAL_SHOWN_KEY);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch {
    return new Set();
  }
};

/**
 * Mark a discount ID as having shown its modal
 * @param {string|number} discountId - The discount ID to mark as shown
 */
export const markDiscountAsShown = (discountId) => {
  try {
    const shown = getShownDiscountIds();
    shown.add(String(discountId));
    sessionStorage.setItem(DISCOUNT_MODAL_SHOWN_KEY, JSON.stringify([...shown]));
  } catch {
    // Silently fail if sessionStorage is unavailable
  }
};

/**
 * Check if a discount modal has already been shown
 * @param {string|number} discountId - The discount ID to check
 * @returns {boolean} True if the modal has already been shown
 */
export const hasDiscountBeenShown = (discountId) => {
  const shown = getShownDiscountIds();
  return shown.has(String(discountId));
};

/**
 * Clear all shown discount IDs (useful for testing)
 */
export const clearShownDiscountIds = () => {
  try {
    sessionStorage.removeItem(DISCOUNT_MODAL_SHOWN_KEY);
  } catch {
    // Silently fail if sessionStorage is unavailable
  }
};
